import is from '@sindresorhus/is';
import clsx from 'clsx';
import type { FunctionComponent, ReactNode } from 'react';

import { Button } from '../button/Button';
import { Dialog } from '../dialog/Dialog';
import { Switch } from '../switch/Switch';

export type DontShowAgainProps = {
    readonly checked: boolean;
    readonly label: string;
    readonly onChange: (checked: boolean) => void;
};

export type ConfirmationDialogProps = {
    readonly cancelButtonLabel: string;
    readonly confirmButtonLabel: string;
    readonly confirmButtonVariant: 'default' | 'destructive' | 'secondary';
    readonly description: ReactNode | string;
    readonly dontShowAgain?: DontShowAgainProps;
    readonly onCancel: () => void;
    readonly onConfirm: () => void;
    readonly open: boolean;
    readonly renderCancelButton?: (onCancel: () => void) => ReactNode;
    readonly renderConfirmButton?: (onConfirm: () => void) => ReactNode;
    readonly showCloseButton?: boolean;
    readonly title: ReactNode | string;
};

const DontShowAgainSwitch: FunctionComponent<DontShowAgainProps> = (props: DontShowAgainProps) => {
    const { checked, label, onChange } = props;

    return (
        <label className="flex items-center gap-m cursor-pointer" htmlFor="dont-show-again-switch">
            <Switch checked={checked} id="dont-show-again-switch" onCheckedChange={onChange} />
            <span className="text-sm font-normal leading-[1.25rem] text-gray-700">{label}</span>
        </label>
    );
};

export const ConfirmationDialog: FunctionComponent<ConfirmationDialogProps> = ({
    cancelButtonLabel,
    confirmButtonLabel,
    confirmButtonVariant,
    description,
    dontShowAgain,
    onCancel,
    onConfirm,
    open,
    renderCancelButton,
    renderConfirmButton,
    showCloseButton = true,
    title,
}) => {
    const defaultCancelButton = (
        <Button variant="tertiary" onClick={onCancel}>
            {cancelButtonLabel}
        </Button>
    );

    const defaultConfirmButton = (
        <Button variant={confirmButtonVariant} onClick={onConfirm}>
            {confirmButtonLabel}
        </Button>
    );

    return (
        <Dialog.Root
            open={open}
            onOpenChange={(isOpen) => {
                if (isOpen === false) {
                    onCancel();
                }
            }}
        >
            <Dialog.Portal>
                <Dialog.Backdrop />
                <Dialog.Content
                    className={clsx('flex flex-col gap-xl max-w-[367px] p-xxl')}
                    data-testid="confirmation-dialog-content"
                >
                    <Dialog.Title>{title}</Dialog.Title>
                    <Dialog.Description>{description}</Dialog.Description>
                    {!is.undefined(dontShowAgain) && (
                        <DontShowAgainSwitch
                            checked={dontShowAgain.checked}
                            label={dontShowAgain.label}
                            onChange={dontShowAgain.onChange}
                        />
                    )}
                    <div className="flex gap-s justify-end">
                        {renderCancelButton !== undefined ? renderCancelButton(onCancel) : defaultCancelButton}
                        {renderConfirmButton !== undefined ? renderConfirmButton(onConfirm) : defaultConfirmButton}
                    </div>
                    {showCloseButton && <Dialog.Close>✕</Dialog.Close>}
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};
